import { useRef, useState, useCallback } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useIsMutating } from "@tanstack/react-query";
import {
    ActionButton,
    Button,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    RadioInput,
    Select,
    TextArea,
} from "@jhool-io/fe-components";

import { parseISO } from "date-fns";

import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import { RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS } from "../../../../utils/constants";
import {
    ISessionNote,
    TypeOfDisclosure,
    MethodOfDisclosure,
    IGetClientNotes,
    IEditRecordOfDisclosurePsychPayload,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import * as Progress from "../../../../shared-ui/Progress/Progress";
import DeleteIcon from "../../../../icons/Delete";
import { IViewFile } from "../../../../utils/types";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

interface EditRecordOfDisclosurePsychProps {
    noteDetails: ISessionNote;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function EditRecordOfDisclosurePsych({
    noteDetails,
}: EditRecordOfDisclosurePsychProps) {
    const [
        localRecordOfDisclosureOfPhiEphi,
        setLocalRecordOfDisclosureOfPhiEphi,
    ] = useState<Date | null>(
        noteDetails.note_content?.record_of_disclosure_of_phi_ephi
            ? parseISO(
                  noteDetails.note_content.record_of_disclosure_of_phi_ephi
              )
            : null
    );
    const [docInView, setDocInView] = useState<IViewFile | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);

    const isSigning = useRef(false);

    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);

    const {
        handleAutoSaveNote,
        handleEditDraftNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    } = useEditNote();

    const isAddDocMutating = useIsMutating(["add-note-content-document"]);
    const isDeleteDocMutating = useIsMutating(["delete-note-content-document"]);

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };
    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        register,
        handleSubmit,
        getValues,
        control,
        setValue,
        watch,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditRecordOfDisclosurePsychPayload, { isDraft: boolean }>({
        resolver: yupResolver(
            yup.object({
                duration: yup.string(),
                diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .nullable(),
                }),
                secondary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                tertiary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_one: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_two: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                disclosure_to_whom: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Disclosure to whom is required"),
                    otherwise: yup.string(),
                }),
                disclosure_type: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Disclosure type is required"),
                    otherwise: yup.string().nullable(),
                }),
                purpose_of_disclosure: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Purpose of disclosure is required"),
                    otherwise: yup.string(),
                }),
                type_disclosed: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Type disclosed is required"),
                    otherwise: yup.string(),
                }),
                method_of_disclosure: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Method of disclosure is required"),
                    otherwise: yup.string(),
                }),
                other_method_of_disclosure: yup
                    .string()
                    .when(
                        "method_of_disclosure",
                        (method_of_disclosure, field) =>
                            method_of_disclosure === "Other" &&
                            isSigning.current
                                ? field.required(
                                      "Other method of disclosure is required"
                                  )
                                : field
                    ),
                record_of_disclosure_of_phi_ephi: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup.date().required("Field is required"),
                    otherwise: yup.date().nullable(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    // Get methods of disclosure for select
    const MethodsOfDisclosureForSelect = Object.values(MethodOfDisclosure).map(
        (method) => {
            return {
                label: method,
                value: method,
            };
        }
    );

    // Get types of disclosure for select
    const TypesOfDisclosureForSelect = Object.values(TypeOfDisclosure).map(
        (form) => {
            return {
                label: form,
                value: form,
            };
        }
    );

    // Function to handle item click and set the selected note content
    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue("secondary_diagnosis", [
            note.note_content.secondary_diagnosis,
        ]);
        setValue("tertiary_diagnosis", [note.note_content.tertiary_diagnosis]);
        setValue("additional_diagnosis_one", [
            note.note_content.additional_diagnosis_one,
        ]);
        setValue("additional_diagnosis_two", [
            note.note_content.additional_diagnosis_two,
        ]);
        setValue("disclosure_to_whom", note.note_content.disclosure_to_whom);
        setValue("disclosure_type", note.note_content.disclosure_type);
        setValue("duration", note.note_content.duration);
        setValue(
            "method_of_disclosure",
            note.note_content.method_of_disclosure
        );
        setValue(
            "other_method_of_disclosure",
            note.note_content.other_method_of_disclosure
        );
        setValue(
            "purpose_of_disclosure",
            note.note_content.purpose_of_disclosure
        );
        setValue("type_disclosed", note.note_content.type_disclosed);
        setValue(
            "record_of_disclosure_of_phi_ephi",
            note.note_content.record_of_disclosure_of_phi_ephi
        );
        setShowPrompt(false);
    };

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else {
                setSelectedFiles([...acceptedFiles]);
            }
        },
        [selectedFiles]
    );

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <div>
            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}
            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="edit-form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>

                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.diagnosis_codes
                                }
                                value={field.value}
                                onChange={field.onChange}
                                isRequired
                                errorText={
                                    errors.diagnosis_codes?.type === "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors.diagnosis_codes?.message
                                }
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("disclosure_to_whom")}
                        label={
                            RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.disclosure_to_whom
                        }
                        defaultValue={
                            noteDetails.note_content?.disclosure_to_whom
                        }
                        hasError={!!errors.disclosure_to_whom}
                        errorText={errors.disclosure_to_whom?.message}
                        isRequired
                    />
                </div>
                <p className="note-label-req">
                    {RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.disclosure_type}
                </p>
                <div className="fg fg-space-between two flex">
                    <RadioInput
                        {...register("disclosure_type")}
                        label="Authorized disclosure"
                        value="Authorized disclosure"
                        defaultChecked={
                            noteDetails.note_content.disclosure_type ===
                            "Authorized disclosure"
                        }
                    />

                    <RadioInput
                        {...register("disclosure_type")}
                        label="Unauthorized disclosure"
                        value="Unauthorized disclosure"
                        defaultChecked={
                            noteDetails.note_content.disclosure_type ===
                            "Unauthorized disclosure"
                        }
                    />
                </div>
                {errors.disclosure_type ? (
                    <div className="note-error">
                        <p>Disclosure type is required</p>
                    </div>
                ) : null}
                <div className="fg">
                    <TextArea
                        {...register("purpose_of_disclosure")}
                        label={
                            RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.purpose_of_disclosure
                        }
                        defaultValue={
                            noteDetails.note_content?.purpose_of_disclosure
                        }
                        hasError={!!errors.purpose_of_disclosure}
                        errorText={errors.purpose_of_disclosure?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="type_disclosed"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label={
                                    RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.type_disclosed
                                }
                                placeholder=""
                                options={TypesOfDisclosureForSelect}
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                hasError={!!errors.type_disclosed}
                                errorText={errors.type_disclosed?.message}
                                value={
                                    TypesOfDisclosureForSelect?.find(
                                        (disclosed) =>
                                            disclosed.value === field.value
                                    ) || null
                                }
                                isRequired
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="method_of_disclosure"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label={
                                    RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.method_of_disclosure
                                }
                                placeholder=""
                                options={MethodsOfDisclosureForSelect}
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                hasError={!!errors.method_of_disclosure}
                                errorText={errors.method_of_disclosure?.message}
                                value={
                                    MethodsOfDisclosureForSelect.find(
                                        (method) => method.value === field.value
                                    ) || null
                                }
                                isRequired
                            />
                        )}
                    />
                </div>
                {watch(`method_of_disclosure`) === "Other" ||
                noteDetails.note_content?.method_of_disclosure === "Other" ? (
                    <div className="fg">
                        <Input
                            {...register(`other_method_of_disclosure`)}
                            label={
                                RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.other_method_of_disclosure
                            }
                            hasError={!!errors?.other_method_of_disclosure}
                            errorText={
                                errors?.other_method_of_disclosure?.message
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.other_method_of_disclosure
                            }
                        />
                    </div>
                ) : null}
                <div className="fg">
                    <Controller
                        name="record_of_disclosure_of_phi_ephi"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                isRequired
                                label={
                                    RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.record_of_disclosure_of_phi_ephi
                                }
                                onChange={(date) => {
                                    field.onChange(date);
                                    setLocalRecordOfDisclosureOfPhiEphi(date);
                                }}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                selected={localRecordOfDisclosureOfPhiEphi}
                                hasError={
                                    !!errors.record_of_disclosure_of_phi_ephi
                                }
                                errorText="Field is required"
                            />
                        )}
                    />
                </div>
                <h3 className="note-label-opt">
                    {RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS.files}
                </h3>
                <div className="fg flex flex-col gap-y-[--spacing-form-items]">
                    <FilePicker.FilePicker
                        onDrop={onDrop}
                        disabled={isAddDocMutating > 0}
                    >
                        <FilePicker.FilePickerIcon />
                        <FilePicker.FilePickerMeta description="Click to upload note files" />
                    </FilePicker.FilePicker>
                    {selectedFiles && selectedFiles.length > 0 ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            <FilePicker.FilePickerSelectedFiles
                                selectedFiles={selectedFiles}
                                handleRemoveSelectedFile={
                                    handleRemoveSelectedFile
                                }
                                wrapperClassName="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] grid-flow-row flex-row gap-[--spacing-items]"
                            />
                            <Button
                                className="self-start"
                                onClick={() =>
                                    handleAddNoteDocument(selectedFiles, () =>
                                        setSelectedFiles(null)
                                    )
                                }
                                disabled={isAddDocMutating > 0}
                            >
                                Save files
                            </Button>
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <Progress.Root value={uploadProgress}>
                                    <Progress.Label>
                                        Uploading {selectedFiles.length}{" "}
                                        {selectedFiles.length === 1
                                            ? "file"
                                            : "files"}
                                        ...
                                    </Progress.Label>
                                    <Progress.Value />
                                    <Progress.Track>
                                        <Progress.Indicator />
                                    </Progress.Track>
                                </Progress.Root>
                            )}
                        </div>
                    ) : null}
                    {noteDetails?.note_content_documents &&
                    noteDetails?.note_content_documents.length > 0 ? (
                        <div className="flex flex-col gap-y-[var(--spacing-items)]">
                            {noteDetails.note_content_documents.map((doc) => (
                                <div
                                    key={doc.note_content_document_id}
                                    className="flex items-center gap-x-3"
                                >
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        className="h-7 !text-text text-caption underline"
                                        actionTitle="View file"
                                        onClick={() => {
                                            setDocInView(doc);
                                        }}
                                    >
                                        <span>{`${displayNameInRightFormat(
                                            {
                                                firstName:
                                                    doc.uploaded_by.first_name,
                                                lastName:
                                                    doc.uploaded_by.last_name,
                                            },
                                            false
                                        )} -  ${doc.document_name}`}</span>
                                    </ActionButton>
                                    <ActionButton
                                        variant="can-hover"
                                        aria-label="delete icon"
                                        size="auto"
                                        className="h-7"
                                        onClick={() =>
                                            handleDeleteNoteContentDocument(
                                                doc.note_content_document_id
                                            )
                                        }
                                        disabled={isDeleteDocMutating > 0}
                                        actionTitle="Delete file from note content"
                                    >
                                        <DeleteIcon />
                                    </ActionButton>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </div>
    );
}
